import React, { useState, useEffect } from 'react';
import { useBaseUrl } from '../utils/utils';
import localforage from 'localforage';
import Swal from 'sweetalert2';
import ConsentModal from './ServiceRequestModals/ConsentModal';
import ApplicationApprovalModal from './ServiceRequestModals/ApplicationApprovalModal';
import ServiceRequestDetailsModal from './ServiceRequestModals/ServiceRequestDetailsModal';

interface ServiceRequest {
    id: number;
    title: string;
    description: string;
    requesting_clinic: string;
    status: string;
    request_type: 'direct' | 'open';
    priority: 'low' | 'normal' | 'high';
    created: string;
    applications_count: number;
    patient_consent_given: boolean;
    patient_consent_date?: string;
    share_patient_demographics: boolean;
    share_medical_history: boolean;
    share_current_medications: boolean;
    share_lab_results: boolean;
    share_imaging: boolean;
    share_notes: boolean;
}

interface ServiceApplication {
    id: number;
    partner_name: string;
    partner_specialty: string;
    partner_experience_years: number;
    partner_certifications: string;
    partner_equipment: string;
    status: string;
    partner_minimum: number;
    partner_maximum: number;
    final_rate?: number;
    patient_approved: boolean;
    patient_notes?: string;
    created: string;
}

interface NegotiationMessage {
    id: number;
    sender_name: string;
    message: string;
    message_type: string;
    proposed_rate?: number;
    created: string;
}

interface ConsentRequest {
    id: number;
    consent_type: string;
    description: string;
    required: boolean;
    status: 'pending' | 'given' | 'denied';
}

interface Activity {
    id: number;
    service_request_id: number;
    activity_type: string;
    description: string;
    created: string;
    performed_by_clinic: string | null;
    performed_by_patient: string | null;
}

const PatientServiceRequests: React.FC = () => {
    const [activeTab, setActiveTab] = useState<'requests' | 'applications' | 'consents' | 'activities'>('requests');
    const [serviceRequests, setServiceRequests] = useState<ServiceRequest[]>([]);
    const [applications, setApplications] = useState<ServiceApplication[]>([]);
    const [negotiations, setNegotiations] = useState<NegotiationMessage[]>([]);
    const [consentRequests, setConsentRequests] = useState<ConsentRequest[]>([]);
    const [activities, setActivities] = useState<Activity[]>([]);
    const [selectedConsent, setSelectedConsent] = useState<ConsentRequest | null>(null);
    const [loading, setLoading] = useState(false);
    // Patient.patient_id (backend) is a 7-character code, not a number -
    // this was typed `number` and cast with `as number` below, which
    // would have sent the wrong shape to every endpoint that filters by it.
    const [patientId, setPatientId] = useState<string | null>(null);
    const [selectedRequest, setSelectedRequest] = useState<ServiceRequest | null>(null);
    const [selectedApplication, setSelectedApplication] = useState<ServiceApplication | null>(null);
    const [showConsentModal, setShowConsentModal] = useState(false);
    const [showApplicationModal, setShowApplicationModal] = useState(false);
    
    const base_url = useBaseUrl();

    useEffect(() => {
        loadPatientId();
        fetchData();
    }, [activeTab]);

    const loadPatientId = async () => {
        const storedPatientId = await localforage.getItem('patientId');
        setPatientId(storedPatientId as string);
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            switch (activeTab) {
                case 'requests':
                    await fetchServiceRequests();
                    break;
                case 'applications':
                    await fetchApplications();
                    break;
                case 'consents':
                    await fetchConsentRequests();
                    break;
                case 'activities':
                    await fetchActivities();
                    break;
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            Swal.fire('Error', 'Failed to load data', 'error');
        } finally {
            setLoading(false);
        }
    };

    const fetchServiceRequests = async () => {
        if (!patientId) return;
        
        const response = await fetch(`${base_url}collaborations/service-requests/patient-requests/`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ patient_id: patientId })
        });
        
        const data = await response.json();
        if (data.status === 'success') {
            setServiceRequests(data.data);
        }
    };

    const fetchApplications = async () => {
        if (!patientId) return;
        
        const response = await fetch(`${base_url}collaborations/applications/patient-applications/`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ patient_id: patientId })
        });
        
        const data = await response.json();
        if (data.status === 'success') {
            setApplications(data.data);
        }
    };

    const fetchConsentRequests = async () => {
        if (!patientId) return;
        
        const response = await fetch(`${base_url}collaborations/consents/patient-consents/`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ patient_id: patientId })
        });
        
        const data = await response.json();
        if (data.status === 'success') {
            setConsentRequests(data.data);
        }
    };

    const fetchActivities = async () => {
        if (!patientId) return;
        
        const response = await fetch(`${base_url}collaborations/activities/patient-activities/`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ patient_id: patientId })
        });
        
        const data = await response.json();
        if (data.status === 'success') {
            // Previously fetched and immediately discarded the response -
            // the Activities tab always rendered a static "will appear
            // here" placeholder regardless of what this call returned.
            setActivities(data.data);
        }
    };

    const getStatusBadge = (status: string) => {
        const statusConfig = {
            draft: { color: 'bg-secondary', text: 'Draft' },
            pending: { color: 'bg-warning', text: 'Pending' },
            reviewing: { color: 'bg-info', text: 'Reviewing' },
            negotiating: { color: 'bg-primary', text: 'Negotiating' },
            patient_approval: { color: 'bg-warning', text: 'Your Approval Needed' },
            agreed: { color: 'bg-success', text: 'Agreed' },
            rejected: { color: 'bg-danger', text: 'Rejected' },
            cancelled: { color: 'bg-dark', text: 'Cancelled' },
            upgraded: { color: 'bg-success', text: 'Collaboration' }
        };
        
        const config = statusConfig[status as keyof typeof statusConfig] || { color: 'bg-secondary', text: status };
        return <span className={`badge ${config.color} text-white`}>{config.text}</span>;
    };

    const getPriorityBadge = (priority: string) => {
        const priorityConfig = {
            low: { color: 'bg-success', text: 'Low' },
            normal: { color: 'bg-warning', text: 'Normal' },
            high: { color: 'bg-danger', text: 'High' }
        };
        
        // Previously had no fallback - any priority value other than
        // exactly 'low'/'normal'/'high' left `config` undefined and crashed
        // the whole component's render (not just this badge) on
        // `config.color`.
        const config = priorityConfig[priority as keyof typeof priorityConfig] || {color: 'bg-secondary', text: priority};
        return <span className={`badge ${config.color} text-white`}>{config.text}</span>;
    };

    const handleConsentResponse = async (consentId: number, consentGiven: boolean, sharingPreferences: any) => {
        try {
            const response = await fetch(`${base_url}collaborations/consents/respond/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    consent_id: consentId,
                    patient_id: patientId,
                    consent_given: consentGiven,
                    ...sharingPreferences
                })
            });
            
            const data = await response.json();
            if (data.status === 'success') {
                Swal.fire('Success', 'Consent response recorded', 'success');
                fetchConsentRequests();
            } else {
                Swal.fire('Error', data.message || 'Failed to record consent', 'error');
            }
        } catch (error) {
            Swal.fire('Error', 'Failed to record consent response', 'error');
        }
    };

    const handleApplicationApproval = async (applicationId: number, approved: boolean, notes?: string) => {
        try {
            const response = await fetch(`${base_url}collaborations/applications/${applicationId}/patient-approval/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    patient_id: patientId,
                    patient_approved: approved,
                    patient_notes: notes || ''
                })
            });
            
            const data = await response.json();
            if (data.status === 'success') {
                Swal.fire('Success', `Partner ${approved ? 'approved' : 'rejected'}`, 'success');
                fetchApplications();
            } else {
                Swal.fire('Error', data.message || 'Failed to record approval', 'error');
            }
        } catch (error) {
            Swal.fire('Error', 'Failed to record approval', 'error');
        }
    };

    return (
        <div className="container-fluid">
            {/* Header */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="d-flex justify-content-between align-items-center">
                        <h4 className="mb-0">
                            <i className="fas fa-user-shield me-2"></i>
                            My Service Requests
                        </h4>
                        <div className="d-flex gap-2">
                            <button className="btn btn-outline-primary btn-sm">
                                <i className="fas fa-question-circle me-2"></i>
                                Help
                            </button>
                        </div>
                    </div>
                    <p className="text-muted mb-0">
                        Manage your healthcare service requests and control your information sharing
                    </p>
                </div>
            </div>

            {/* Tabs */}
            <div className="row mb-4">
                <div className="col-12">
                    <ul className="nav nav-tabs nav-tabs-custom" role="tablist">
                        <li className="nav-item">
                            <button 
                                className={`nav-link ${activeTab === 'requests' ? 'active' : ''}`}
                                onClick={() => setActiveTab('requests')}
                            >
                                <i className="fas fa-list me-2"></i>
                                Service Requests
                                {serviceRequests.filter(r => r.status === 'patient_approval').length > 0 && (
                                    <span className="badge bg-warning ms-2">
                                        {serviceRequests.filter(r => r.status === 'patient_approval').length}
                                    </span>
                                )}
                            </button>
                        </li>
                        <li className="nav-item">
                            <button 
                                className={`nav-link ${activeTab === 'applications' ? 'active' : ''}`}
                                onClick={() => setActiveTab('applications')}
                            >
                                <i className="fas fa-file-alt me-2"></i>
                                Partner Applications
                                {applications.filter(a => !a.patient_approved && a.status === 'accepted').length > 0 && (
                                    <span className="badge bg-warning ms-2">
                                        {applications.filter(a => !a.patient_approved && a.status === 'accepted').length}
                                    </span>
                                )}
                            </button>
                        </li>
                        <li className="nav-item">
                            <button 
                                className={`nav-link ${activeTab === 'consents' ? 'active' : ''}`}
                                onClick={() => setActiveTab('consents')}
                            >
                                <i className="fas fa-shield-alt me-2"></i>
                                Consent Requests
                                {consentRequests.filter(c => c.status === 'pending').length > 0 && (
                                    <span className="badge bg-warning ms-2">
                                        {consentRequests.filter(c => c.status === 'pending').length}
                                    </span>
                                )}
                            </button>
                        </li>
                        <li className="nav-item">
                            <button 
                                className={`nav-link ${activeTab === 'activities' ? 'active' : ''}`}
                                onClick={() => setActiveTab('activities')}
                            >
                                <i className="fas fa-history me-2"></i>
                                Activity Timeline
                            </button>
                        </li>
                    </ul>
                </div>
            </div>

            {/* Content */}
            <div className="row">
                <div className="col-12">
                    {loading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : (
                        <div className="tab-content">
                            {/* Service Requests */}
                            {activeTab === 'requests' && (
                                <div className="tab-pane fade show active">
                                    <div className="row">
                                        {serviceRequests.map((request) => (
                                            <div key={request.id} className="col-lg-6 col-xl-4 mb-4">
                                                <div className={`card h-100 border-0 shadow-sm ${request.status === 'patient_approval' ? 'border-warning border-2' : ''}`}>
                                                    <div className="card-header bg-transparent border-0">
                                                        <div className="d-flex justify-content-between align-items-start">
                                                            <h6 className="card-title mb-0 text-truncate">
                                                                {request.title}
                                                            </h6>
                                                            <div className="d-flex gap-1">
                                                                {getStatusBadge(request.status)}
                                                                {getPriorityBadge(request.priority)}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="card-body">
                                                        <p className="text-muted small mb-3">
                                                            {request.description.substring(0, 100)}...
                                                        </p>
                                                        <div className="row g-2 mb-3">
                                                            <div className="col-6">
                                                                <small className="text-muted">Requesting Clinic:</small>
                                                                <div className="fw-medium">{request.requesting_clinic}</div>
                                                            </div>
                                                            <div className="col-6">
                                                                <small className="text-muted">Type:</small>
                                                                <div className="fw-medium text-capitalize">{request.request_type}</div>
                                                            </div>
                                                            <div className="col-6">
                                                                <small className="text-muted">Applications:</small>
                                                                <div className="fw-medium">{request.applications_count}</div>
                                                            </div>
                                                            <div className="col-6">
                                                                <small className="text-muted">Your Consent:</small>
                                                                <div className="fw-medium">
                                                                    {request.patient_consent_given ? 
                                                                        <span className="text-success">✓ Given</span> : 
                                                                        <span className="text-warning">⏳ Pending</span>
                                                                    }
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="d-flex justify-content-between align-items-center">
                                                            <small className="text-muted">
                                                                Created: {new Date(request.created).toLocaleDateString()}
                                                            </small>
                                                            <button 
                                                                className="btn btn-sm btn-outline-primary"
                                                                onClick={() => setSelectedRequest(request)}
                                                            >
                                                                View Details
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Applications */}
                            {activeTab === 'applications' && (
                                <div className="tab-pane fade show active">
                                    <div className="table-responsive">
                                        <table className="table table-hover">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>Partner</th>
                                                    <th>Specialty</th>
                                                    <th>Experience</th>
                                                    <th>Proposed Rate</th>
                                                    <th>Status</th>
                                                    <th>Your Approval</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {applications.map((app) => (
                                                    <tr key={app.id}>
                                                        <td>
                                                            <div className="fw-medium">{app.partner_name}</div>
                                                            <small className="text-muted">{app.partner_certifications}</small>
                                                        </td>
                                                        <td>
                                                            <div className="fw-medium">{app.partner_specialty}</div>
                                                            <small className="text-muted">{app.partner_equipment}</small>
                                                        </td>
                                                        <td>
                                                            <div className="fw-medium">{app.partner_experience_years} years</div>
                                                        </td>
                                                        <td>
                                                            <div className="fw-medium">
                                                                ${app.final_rate || app.partner_minimum} - ${app.partner_maximum}
                                                            </div>
                                                        </td>
                                                        <td>{getStatusBadge(app.status)}</td>
                                                        <td>
                                                            {app.patient_approved ? 
                                                                <span className="text-success">✓ Approved</span> : 
                                                                app.status === 'accepted' ? 
                                                                    <span className="text-warning">⏳ Pending</span> :
                                                                    <span className="text-muted">-</span>
                                                            }
                                                        </td>
                                                        <td>
                                                            <div className="btn-group btn-group-sm">
                                                                <button 
                                                                    className="btn btn-outline-primary"
                                                                    onClick={() => setSelectedApplication(app)}
                                                                >
                                                                    View
                                                                </button>
                                                                {app.status === 'accepted' && !app.patient_approved && (
                                                                    <button 
                                                                        className="btn btn-outline-success"
                                                                        onClick={() => setShowApplicationModal(true)}
                                                                    >
                                                                        Approve
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {/* Consent Requests */}
                            {activeTab === 'consents' && (
                                <div className="tab-pane fade show active">
                                    <div className="row">
                                        {consentRequests.map((consent) => (
                                            <div key={consent.id} className="col-lg-6 mb-4">
                                                <div className={`card border-0 shadow-sm ${consent.status === 'pending' ? 'border-warning border-2' : ''}`}>
                                                    <div className="card-header bg-transparent">
                                                        <div className="d-flex justify-content-between align-items-center">
                                                            <h6 className="mb-0">
                                                                <i className="fas fa-shield-alt me-2"></i>
                                                                {consent.consent_type.replace('_', ' ').toUpperCase()}
                                                            </h6>
                                                            <span className={`badge ${consent.status === 'pending' ? 'bg-warning' : consent.status === 'given' ? 'bg-success' : 'bg-danger'}`}>
                                                                {consent.status.toUpperCase()}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="card-body">
                                                        <p className="mb-3">{consent.description}</p>
                                                        {consent.status === 'pending' && (
                                                            <div className="d-flex gap-2">
                                                                <button
                                                                    className="btn btn-success btn-sm"
                                                                    onClick={() => {
                                                                        // Previously opened the modal with no
                                                                        // reference to which consent it was for -
                                                                        // ConsentModal defaults consentId to 0,
                                                                        // so every "Give Consent" click regardless
                                                                        // of card would have tried to respond to
                                                                        // a nonexistent consent id 0.
                                                                        setSelectedConsent(consent);
                                                                        setShowConsentModal(true);
                                                                    }}
                                                                >
                                                                    <i className="fas fa-check me-2"></i>
                                                                    Give Consent
                                                                </button>
                                                                <button 
                                                                    className="btn btn-danger btn-sm"
                                                                    onClick={() => handleConsentResponse(consent.id, false, {})}
                                                                >
                                                                    <i className="fas fa-times me-2"></i>
                                                                    Deny
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Activity Timeline */}
                            {activeTab === 'activities' && (
                                <div className="tab-pane fade show active">
                                    {activities.length === 0 ? (
                                        <div className="text-center py-5">
                                            <i className="fas fa-history fa-3x text-muted mb-3"></i>
                                            <h5>Activity Timeline</h5>
                                            <p className="text-muted">Your service request activities will appear here</p>
                                        </div>
                                    ) : (
                                        <div className="timeline">
                                            {activities.map((activity) => (
                                                <div key={activity.id} className="d-flex gap-3 py-3 border-bottom">
                                                    <div className="flex-shrink-0">
                                                        <i className="fas fa-circle text-primary" style={{fontSize: '0.5rem', marginTop: '0.4rem'}}></i>
                                                    </div>
                                                    <div className="flex-grow-1">
                                                        <p className="mb-1">{activity.description}</p>
                                                        <small className="text-muted">
                                                            {new Date(activity.created).toLocaleString()}
                                                            {activity.performed_by_clinic && ` · ${activity.performed_by_clinic}`}
                                                            {activity.performed_by_patient && ` · ${activity.performed_by_patient}`}
                                                        </small>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Consent Modal */}
            {showConsentModal && (
                <ConsentModal
                    consentId={selectedConsent?.id}
                    onClose={() => {
                        setShowConsentModal(false);
                        setSelectedConsent(null);
                    }}
                    onSubmit={handleConsentResponse}
                />
            )}

            {/* Application Approval Modal */}
            {showApplicationModal && (
                <ApplicationApprovalModal 
                    application={selectedApplication}
                    onClose={() => setShowApplicationModal(false)}
                    onSubmit={handleApplicationApproval}
                />
            )}

            {/* Request Details Modal */}
            {selectedRequest && (
                <ServiceRequestDetailsModal 
                    request={selectedRequest}
                    onClose={() => setSelectedRequest(null)}
                />
            )}
        </div>
    );
};

export default PatientServiceRequests; 